/**
 * Coach Code Utilities
 * Handles coach code generation and validation
 */

import { supabase } from '../supabase';

/**
 * Validate coach code format
 * @param {string} code - Coach code to validate
 * @returns {boolean} - True if valid format
 */
export const isValidCoachCodeFormat = (code) => {
  if (!code) return false;
  // Format: C-NAME, A-NAME, SA-NAME (with optional number suffix)
  // Examples: C-JOHN, C-JOHN2, A-MIKE, SA-ADMIN
  const regex = /^(C|A|SA)-[A-Z]+\d*$/;
  return regex.test(code);
};

/**
 * Look up coach by coach code
 * @param {string} coachCode - The coach code to look up
 * @returns {Promise<{success: boolean, coach?: object, error?: string}>}
 */
export const lookupCoachByCode = async (coachCode) => {
  try {
    if (!coachCode || !coachCode.trim()) {
      return { success: false, error: 'Coach code is required' };
    }

    const normalizedCode = coachCode.trim().toUpperCase();

    // Signup is anonymous at this point (auth account isn't created until the
    // final step), and no RLS policy permits anonymous reads of `users` — so
    // this goes through a SECURITY DEFINER RPC that returns only the minimal
    // non-sensitive fields the signup UI needs, same pattern as get_super_admin_id().
    const { data, error } = await supabase.rpc('lookup_coach_by_code', { p_code: normalizedCode });

    if (error) {
      throw error;
    }

    const coach = data?.[0];

    if (!coach) {
      return { success: false, error: 'Coach code not found. Please check and try again.' };
    }

    if (!['coach', 'admin', 'super_admin'].includes(coach.role)) {
      return { success: false, error: 'Invalid coach code' };
    }

    return { success: true, coach };
  } catch (err) {
    console.error('Error looking up coach code:', err);
    return { success: false, error: 'Failed to validate coach code. Please try again.' };
  }
};

/**
 * Generate coach code from name (client-side preview)
 * Note: Actual generation happens server-side to ensure uniqueness
 * @param {string} name - Full name or first name
 * @param {string} role - User role (coach, admin, super_admin)
 * @returns {string} - Base coach code (without number suffix)
 */
export const generateCoachCodePreview = (name, role = 'coach') => {
  const cleanName = (name) => {
    return name
      .replace(/[^a-zA-Z]/g, '')
      .toUpperCase();
  };

  // Extract first name (first word)
  const firstName = (name || '').split(' ')[0];
  const cleanedName = cleanName(firstName);

  // Determine prefix based on role
  let prefix = 'C'; // coach
  if (role === 'admin') prefix = 'A';
  if (role === 'super_admin') prefix = 'SA';

  return `${prefix}-${cleanedName}`;
};

/**
 * Format coach code for display
 * @param {string} coachCode
 * @returns {string} - Formatted coach code
 */
export const formatCoachCode = (coachCode) => {
  if (!coachCode) return '';
  return coachCode.toUpperCase();
};

/**
 * Get all coaches with codes (for admin use)
 * @returns {Promise<Array>} - List of coaches with codes
 */
export const getAllCoachesWithCodes = async () => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, email, role, coach_code, status, admin_id')
      .in('role', ['coach', 'admin', 'super_admin'])
      .not('coach_code', 'is', null)
      .order('name');

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Error fetching coaches:', err);
    return [];
  }
};

/**
 * Assign client to coach using coach code
 * @param {number} clientUserId - Client's user ID
 * @param {string} coachCode - Coach code
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const assignClientToCoachByCode = async (clientUserId, coachCode) => {
  try {
    // First, look up the coach
    const { success, coach, error } = await lookupCoachByCode(coachCode);

    if (!success) {
      return { success: false, error };
    }

    // Update client's coach_id (coach_id references auth_user_id, not the coach's row id)
    const { error: updateError } = await supabase
      .from('users')
      .update({ coach_id: coach.auth_user_id })
      .eq('id', clientUserId);

    if (updateError) {
      throw updateError;
    }

    return { success: true, coach };
  } catch (err) {
    console.error('Error assigning client to coach:', err);
    return { success: false, error: 'Failed to assign coach. Please try again.' };
  }
};

/**
 * Get coach hierarchy information
 * @param {number} userId - User ID to get hierarchy for
 * @returns {Promise<object>} - Hierarchy information
 */
export const getCoachHierarchy = async (userId) => {
  try {
    const { data: user, error } = await supabase
      .from('users')
      .select(`
        id,
        name,
        role,
        coach_code,
        admin_id,
        coach:admin_id (
          id,
          name,
          email,
          coach_code
        )
      `)
      .eq('id', userId)
      .single();

    if (error) throw error;

    return user;
  } catch (err) {
    console.error('Error fetching coach hierarchy:', err);
    return null;
  }
};
